import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared DOB + 4-layer consent capture (GDPR Week 6).
 * Used on RegisterPage and InviteAcceptPage; MyAccountPage has its own
 * toggle UI driven by /api/me/consents.
 *
 * Age-gating mirrors the server (lib/consents.js — the server re-checks):
 *   <13  — analytics never
 *   <16  — analytics only
 *   <18  — analytics + marketing (no third-party audience extension)
 *   18+  — all three
 * Checkboxes the entered age doesn't permit simply don't render.
 * NOTE (27 Aug 2026): the ACCOUNT floor is currently 18 everywhere (register,
 * invite-accept, DOB backfill) per Stuart — the under-18 consent tiers stay
 * built so the floor can roll back down later without rework.
 */

export const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

export const CONSENT_SCOPES = [
  {
    key: 'analytics',
    minAge: 13,
    label: 'Help improve Marakin',
    detail: 'Helps us see which parts of the app get used and where people get stuck, including replays of screens. Anything you type is always hidden. Kept on secure servers in the EU and never used for advertising.',
  },
  {
    key: 'marketing',
    minAge: 16,
    label: 'Personalised sponsor offers',
    detail: 'Use my activity to show me more relevant club sponsor offers inside Marakin.',
  },
  {
    key: 'audience',
    minAge: 18,
    label: 'Advertising audiences with partners',
    detail: 'Allow Marakin to include my activity when building advertising audiences with platforms like Meta and Google. Never includes under-18s.',
  },
];

const ConsentFields = ({ dob, onDobChange, consents, onConsentsChange, termsAccepted, onTermsChange }) => {
  const age = ageFromDob(dob);

  const eligible = CONSENT_SCOPES.filter(s => age === null || age >= s.minAge);
  const selectedCount = eligible.filter(s => !!consents[s.key]).length;
  const allSelected = eligible.length > 0 && selectedCount === eligible.length;
  const someSelected = selectedCount > 0 && !allSelected;

  // Start collapsed; auto-expand if a partial selection already exists
  const [expanded, setExpanded] = useState(someSelected);
  const masterRef = useRef(null);
  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggle = (key) => (e) =>
    onConsentsChange({ ...consents, [key]: e.target.checked });

  const toggleAll = (e) => {
    const next = { ...consents };
    eligible.forEach(s => { next[s.key] = e.target.checked; });
    onConsentsChange(next);
  };

  const rowStyle = (active) => ({
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
    textTransform: 'none', letterSpacing: 'normal',
    background: active ? 'rgba(30,74,53,0.07)' : 'transparent',
  });

  const checkboxStyle = {
    marginTop: 3, flexShrink: 0, width: 16, height: 16,
    accentColor: 'var(--green, #1E4A35)',
  };

  return (
    <>
      <div className="form-group">
        <label className="label">Date of birth</label>
        <input
          className="input"
          type="date"
          value={dob}
          onChange={onDobChange}
          max={new Date().toISOString().slice(0, 10)}
          required
        />
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Used to apply the right privacy protections for your age. Marakin accounts are for
          adults (18+) — parents and guardians add children and under-18s from their own account.
        </p>
      </div>

      <div className="form-group" style={{ marginTop: 4 }}>
        <label className="label">Your data choices</label>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
          We always store the basics needed to run your club account (name, email, club
          memberships, event responses) — that can't be switched off. Everything else is your
          choice, and you can change it any time in My account.
          {!onTermsChange && (
            <>
              {' '}By creating an account you agree to our{' '}
              <Link to="/terms" target="_blank" style={{ color: 'var(--green)', textDecoration: 'underline' }}>
                terms of service
              </Link>{' '}— see also our{' '}
              <Link to="/privacy" target="_blank" style={{ color: 'var(--green)', textDecoration: 'underline' }}>
                privacy policy
              </Link>.
            </>
          )}
        </p>
        <label style={rowStyle(allSelected)}>
          <input
            ref={masterRef}
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            style={checkboxStyle}
          />
          <span style={{ fontSize: 13.5, lineHeight: 1.35 }}>
            <strong style={{ fontWeight: 700 }}>Accept all optional choices</strong>
            <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12.5 }}>
              {eligible.map(s => s.label).join(' · ')}
            </span>
          </span>
        </label>
        <button
          type="button"
          onClick={() => setExpanded(x => !x)}
          style={{
            background: 'none', border: 'none', padding: '2px 10px 6px',
            color: 'var(--green, #1E4A35)', fontSize: 12.5, fontWeight: 700,
            cursor: 'pointer', textDecoration: 'underline',
            textTransform: 'none', letterSpacing: 'normal',
          }}
        >
          {expanded ? 'Hide choices ▴' : 'Choose individually ▾'}
        </button>
        {expanded && eligible.map(s => (
          <label key={s.key} style={{ ...rowStyle(!!consents[s.key]), marginLeft: 16 }}>
            <input
              type="checkbox"
              checked={!!consents[s.key]}
              onChange={toggle(s.key)}
              style={checkboxStyle}
            />
            <span style={{ fontSize: 13.5, lineHeight: 1.35 }}>
              <strong style={{ fontWeight: 700 }}>{s.label}</strong>
              <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12.5 }}>
                {s.detail}
              </span>
            </span>
          </label>
        ))}
      </div>

      {/* v33: explicit versioned terms clickwrap. Rendered only when the
          parent page wires it up (termsAccepted + onTermsChange) — the old
          by-conduct sentence above stays for any caller that doesn't. */}
      {onTermsChange && (
        <div className="form-group" style={{ marginTop: 4 }}>
          <label style={{ ...rowStyle(!!termsAccepted), border: '1.5px solid ' + (termsAccepted ? 'var(--green, #1E4A35)' : 'var(--border, #E5E0D4)') }}>
            <input
              type="checkbox"
              checked={!!termsAccepted}
              onChange={(e) => onTermsChange(e.target.checked)}
              style={checkboxStyle}
              required
            />
            <span style={{ fontSize: 13.5, lineHeight: 1.4 }}>
              I agree to the Marakin{' '}
              <Link to="/terms" target="_blank" style={{ color: 'var(--green)', textDecoration: 'underline' }}>
                Terms of Service
              </Link>{' '}and have read the{' '}
              <Link to="/privacy" target="_blank" style={{ color: 'var(--green)', textDecoration: 'underline' }}>
                Privacy Policy
              </Link>.
            </span>
          </label>
        </div>
      )}
    </>
  );
};

export default ConsentFields;
